import { Link } from "react-router";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useAuth } from "@/auth/useAuth";
import { dailyApi } from "@/features/daily/api/daily.api";
import { useDailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import { dailyFiltersAdapter, useDailyFilters } from "@/features/daily/hooks/use-daily-filters";
import { DailyResourceStatus, useDailyResource } from "@/features/daily/hooks/use-daily-resource";
import { DailySectionState, DailyStateBlock } from "@/features/daily/components/DailyStateBlock";
import { DailyHeaderMeta } from "@/features/daily/components/DailyHeaderMeta";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import { SkipLink } from "@/features/daily/components/SkipLink";
import { SourceBar } from "@/features/daily/components/SourceBar";
import { JourneyCard } from "@/features/daily/components/funnel/JourneyCard";
import { StageTimesTable } from "@/features/daily/components/funnel/StageTimesTable";
import { funnelViewModel, isFunnelDto } from "@/features/daily/components/funnel/funnel-view-model";
import { commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";
import "@/features/daily/styles/daily.css";

const BODY_ID = "daily-funnel-main";
const POLL_MS = 120_000;

const SECTION_STATE: Record<DailyResourceStatus, DailySectionState> = {
    idle: "loading",
    loading: "loading",
    ok: "ok",
    partial: "partial",
    stale: "stale",
    error: "error",
    forbidden: "forbidden",
};

export function FunnelSlaPage() {
    const { state: authState } = useAuth();
    const { query, set } = useDailyQueryState();
    const { filters, onFiltersChange } = useDailyFilters(query, set, "today");

    const viewerId = authState.user === null ? "" : authState.user.id;

    // L354/FR-009: the window travels as a preset, so a tab left open overnight asks
    // for the new day instead of calling yesterday "today".
    const params = {
        window: query.windowPreset ?? "today",
        from: query.windowPreset === "custom" ? query.windowFrom ?? "" : "",
        to: query.windowPreset === "custom" ? query.windowTo ?? "" : "",
        scope: query.scope.join(","),
        types: query.types.join(","),
    };

    const resource = useDailyResource(
        { resource: "funnel", scope: params.scope, query: params },
        (signal) => dailyApi.getFunnel(params, { signal }),
        { identity: viewerId, isData: isFunnelDto, pollMs: POLL_MS },
    );

    const dto = resource.data;
    const context = dto === null ? null : funnelViewModel.context(dto);
    const stages = dto === null ? [] : funnelViewModel.stages(dto.stages, query);
    const busy = resource.status === "loading" || resource.status === "idle";
    const sectionState: DailySectionState = dto !== null && busy ? "ok" : SECTION_STATE[resource.status];

    const conversion = dto === null ? null : funnelViewModel.conversionCard(dto.conversionJourney);
    const execution = dto === null ? null : funnelViewModel.executionCard(dto.executionJourney);
    const totalStage = dto === null ? undefined : dto.stages.find((stage) => stage.stage === "total_funded");
    const totalVm = stages.find((stage) => stage.stage === "total_funded");
    const compliance = funnelViewModel.compliance(totalStage);
    const scopeParams = { scope: params.scope, types: params.types };
    const inProgressHref = commandViewModel.diagnosticHref({ ...scopeParams, queue: "day", filter: "in_progress" });
    const windowLabel = context === null ? `${dailyFiltersAdapter.periodLabel(filters)} · Europe/Madrid` : `${context.windowLabel} · ${context.timeZone}`;

    return (
        <Shell title="Funnel & SLA" subtitle="Conversión y tiempos">
            <div className="daily-page">
                <SkipLink targetId={BODY_ID} />
                <PageHeader
                    title="Conversión y tiempos"
                    lede="Dónde avanzan las operaciones y dónde esperan."
                    right={(
                        <DailyHeaderMeta
                            chips={{ scope: context === null ? dailyFormat.absent : context.scopeLabel, window: windowLabel }}
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            busy={busy}
                            onRefresh={resource.refresh}
                        />
                    )}
                />

                <div className="daily-body" id={BODY_ID} tabIndex={-1}>
                    <p className="daily-summary">
                        Dos preguntas distintas: <strong>¿el cliente financia?</strong> y <strong>¿entregamos a tiempo?</strong>
                        {context === null ? null : <> · {context.cohortLabel} · Cobertura: {context.coverageLabel}</>}
                    </p>

                    <DailyStateBlock
                        state={sectionState}
                        label="Conversión y tiempos"
                        detail={resource.error === null ? undefined : resource.error.message}
                        onRetry={resource.refresh}
                    >
                        {dto === null || context === null ? null : (
                            <>
                                <div className="daily-columns">
                                    <JourneyCard
                                        title="01 · Conversión comercial"
                                        pill={`Creadas · ${context.windowLabel}`}
                                        card={conversion}
                                        emptyMessage="No se informó ningún paso de conversión para este alcance y ventana."
                                    >
                                        {/* Enmienda C5: the payer's wait is information, never a breach. */}
                                        <p className="daily-small daily-muted">
                                            Espera de fondos del cliente, P95: {context.fundingWaitLabel}. Es información sobre la demanda, no un plazo que la plataforma pueda incumplir.
                                        </p>
                                    </JourneyCard>
                                    <JourneyCard
                                        title="02 · Ejecución financiada"
                                        pill={`Desde fondos · ${context.windowLabel}`}
                                        card={execution}
                                        emptyMessage="No se informó ningún paso de ejecución para este alcance y ventana."
                                    >
                                        {execution === null || execution.pending === null ? null : (
                                            <Link className="daily-link" to={inProgressHref}>
                                                Investigar las {dailyFormat.count(execution.pending)} abiertas <DailyIcon name="arrow_forward" />
                                            </Link>
                                        )}
                                    </JourneyCard>
                                </div>

                                <StageTimesTable stages={stages} />

                                <div className="daily-columns">
                                    <section className="daily-panel" aria-labelledby="daily-compliance-title">
                                        <h2 id="daily-compliance-title">Cumplimiento de plazo</h2>
                                        {compliance === null || totalVm === undefined ? (
                                            <p className="daily-small daily-muted">No hay un plazo total aplicable a este alcance y ventana.</p>
                                        ) : (
                                            <>
                                                <span className="daily-big-value">{compliance.valueLabel}</span>
                                                <p className="daily-small daily-muted">{compliance.detail}</p>
                                                <p className="daily-small">{totalVm.affectedLabel} ({totalVm.breachedOpen} siguen abiertas · {totalVm.breachedLate} terminaron tarde).</p>
                                                {totalVm.reasons.length === 0 ? null : (
                                                    <ul className="daily-reasons">
                                                        {totalVm.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                                                    </ul>
                                                )}
                                            </>
                                        )}
                                    </section>
                                    <section className="daily-panel" aria-labelledby="daily-pending-title">
                                        <h2 id="daily-pending-title">Pendientes ahora</h2>
                                        <span className="daily-big-value">
                                            {execution === null || execution.pending === null
                                                ? dailyFormat.absent
                                                : `${dailyFormat.count(execution.pending)} en curso`}
                                        </span>
                                        <p className="daily-small daily-muted">
                                            {totalVm === undefined
                                                ? "Sin plazo total aplicable."
                                                : `${dailyFormat.counted(totalVm.breachedOpen, "vencida abierta", "vencidas abiertas")} · antigüedad P95 de lo abierto: ${totalVm.openAgeP95Label}.`}
                                        </p>
                                        <p className="daily-small daily-muted">
                                            La antigüedad de las abiertas se muestra aparte de la duración de las completadas.
                                        </p>
                                        <Link className="daily-link" to={inProgressHref}>
                                            Abrir operaciones <DailyIcon name="arrow_forward" />
                                        </Link>
                                    </section>
                                </div>
                            </>
                        )}
                    </DailyStateBlock>

                    {context === null || context.reasons.length === 0 ? null : (
                        <ul className="daily-reasons" aria-label="Motivos de la evidencia">
                            {context.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                        </ul>
                    )}

                    <SourceBar
                        fetchedAt={resource.meta.fetchedAt}
                        sourceAsOf={resource.meta.sourceAsOf}
                        stale={resource.meta.stale}
                        evidenceLabel={context === null ? null : `Evidencia: ${context.qualityLabel} · ${context.policyLabel}`}
                        evidenceTip={null}
                        note="La ejecución se mide desde la recepción de fondos"
                    />
                </div>
            </div>
        </Shell>
    );
}
